import { useEffect, useRef, useState } from 'react'

import { useAnnounce } from '@/shared/providers/announcer-provider'

type ClipboardCopyMessages = {
  successMessage: string
  // Leave this out when the caller shows its own alert on failure, or a screen reader hears the failure twice.
  failureMessage?: string
}

export function useClipboardCopy() {
  const announce = useAnnounce()
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)
  const resetTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(
    () => () => {
      if (resetTimeout.current !== undefined) clearTimeout(resetTimeout.current)
    },
    [],
  )

  function reset() {
    if (resetTimeout.current !== undefined) clearTimeout(resetTimeout.current)
    resetTimeout.current = undefined
    setCopied(false)
    setCopyFailed(false)
  }

  async function copy(value: string, { successMessage, failureMessage }: ClipboardCopyMessages) {
    // Clearing the failure first remounts the caller's alert if this attempt fails too, so it is announced again.
    setCopyFailed(false)
    try {
      await navigator.clipboard.writeText(value)
      if (resetTimeout.current !== undefined) clearTimeout(resetTimeout.current)
      setCopied(true)
      setCopyFailed(false)
      announce(successMessage)
      resetTimeout.current = setTimeout(() => {
        setCopied(false)
        resetTimeout.current = undefined
      }, 2000)
    } catch {
      reset()
      setCopyFailed(true)
      if (failureMessage) announce(failureMessage)
    }
  }

  return { copied, copyFailed, copy, reset }
}
